/** Build-Zeit-Label des aktiven JUDICATURE-Snapshots (`VITE_JUDICATURE_SNAPSHOT`). */

const FULL_SNAPSHOT_RE = /^jud-([a-z0-9_]+)-(\d{4})(\d{2})(\d{2})-v\d+$/i;
const SHORT_SNAPSHOT_RE = /^\d{4}$/;

export interface JudicatureSnapshotInfo {
  /** Rohwert aus Build-Env (z. B. `jud-mietrecht-20260727-v8`). Leer = unbekannt. */
  raw: string;
  /** Kurzlabel für Badge (z. B. `0727`). */
  shortLabel: string;
  /** Anzeige im Portal (z. B. `OGH · 0727`). */
  badgeLabel: string;
  title: string;
  snapshotDate: string | null;
  packageLabel: string;
  /** false = Env fehlt — Stempel bleibt sichtbar, kein Urteil vortäuschen. */
  loaded: boolean;
}

export function getJudicatureSnapshotLabel(): string {
  return getJudicatureSnapshotInfo().badgeLabel;
}

export function resolveJudicatureSnapshotDate(raw: string): string | null {
  const trimmed = raw.trim();
  const full = trimmed.match(FULL_SNAPSHOT_RE);
  if (full) return `${full[4]}.${full[3]}.${full[2]}`;
  const embedded = trimmed.match(/(\d{4})(\d{2})(\d{2})/);
  if (embedded) return `${embedded[3]}.${embedded[2]}.${embedded[1]}`;
  return null;
}

function resolveJudicaturePackageLabel(raw: string): string {
  if (/mietrecht/i.test(raw)) return 'OGH Mietrecht (Pilot)';
  if (/datenschutz/i.test(raw)) return 'OGH Datenschutz';
  if (/medienrecht/i.test(raw)) return 'OGH Medienrecht';
  return 'OGH-Judikatur';
}

export function resolveJudicatureShortLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const full = trimmed.match(FULL_SNAPSHOT_RE);
  if (full) return `${full[3]}${full[4]}`;
  if (SHORT_SNAPSHOT_RE.test(trimmed)) return trimmed;
  const embedded = trimmed.match(/(\d{4})(\d{2})(\d{2})/);
  if (embedded) return `${embedded[2]}${embedded[3]}`;
  return trimmed.length > 8 ? trimmed.slice(-4) : trimmed;
}

/** Immer ein Stempel: mit Snapshot-ID oder ehrlichem „kein Snapshot“. */
export function getJudicatureSnapshotInfo(): JudicatureSnapshotInfo {
  const env = (import.meta as { env?: { VITE_JUDICATURE_SNAPSHOT?: string } }).env;
  const raw = String(env?.VITE_JUDICATURE_SNAPSHOT ?? '').trim();
  if (!raw) {
    return {
      raw: '',
      shortLabel: '—',
      badgeLabel: 'OGH · kein Stand',
      title:
        'OGH-Judikatur-Stand ist in diesem Portal-Stand nicht gesetzt. ' +
        'OGH-Treffer nur, wenn ein aktueller Bestand vorliegt — nichts erfinden.',
      snapshotDate: null,
      packageLabel: 'OGH-Judikatur',
      loaded: false,
    };
  }

  const shortLabel = resolveJudicatureShortLabel(raw) || raw.slice(-4);
  return {
    raw,
    shortLabel,
    badgeLabel: `OGH · ${shortLabel}`,
    title:
      `Aktueller OGH-Judikatur-Stand (${shortLabel}). ` +
      'Recherche in ARKO bezieht sich auf diesen kuratierten Bestand — keine erfundenen Urteile.',
    snapshotDate: resolveJudicatureSnapshotDate(raw),
    packageLabel: resolveJudicaturePackageLabel(raw),
    loaded: true,
  };
}
